// Keeps each Tags page row's last live reading through failed polls, and
// paces the Live poll while reads are failing.

import type { PlantTagIdValue } from '@axilon-apps/domains-ontology-ui-state'

import { type Connectivity, deriveLiveConnectivity, isLiveSourceUnavailable } from './connectivity'

/** Consecutive failed polls a row rides through on its kept reading. */
export const LIVE_FAILURE_GRACE_POLLS = 1
export const LIVE_POLL_INTERVAL_MS = 5_000
export const MAX_LIVE_POLL_INTERVAL_MS = 30_000

interface LiveReadingEntry {
  /** Latest result that wasn't a failure. */
  reading: PlantTagIdValue | undefined
  /** Latest failure, while failures continue. */
  failure: PlantTagIdValue | undefined
  /** Consecutive polls whose result for this tag was a failure. */
  failedPolls: number
}

export interface LiveReadings {
  /** `fulfilledTimeStamp` of the last merged response. */
  mergedAt: number | undefined
  entries: ReadonlyMap<string, LiveReadingEntry>
  /** Consecutive polls with a failed request or a source-unavailable result. */
  failedPolls: number
  /** Consecutive polls with a failed request. */
  transportFailedPolls: number
}

export const EMPTY_LIVE_READINGS: LiveReadings = {
  mergedAt: undefined,
  entries: new Map(),
  failedPolls: 0,
  transportFailedPolls: 0
}

export interface LiveRowState {
  /** Reading whose value and timestamp the row shows. */
  reading: PlantTagIdValue | undefined
  connectivity: Connectivity
  /** Latest failure message, when the row shows a kept reading for a source it couldn't read. */
  failure: string | undefined
}

/**
 * True when a result says the read failed. Unregistered tags and tags still
 * waiting for their first value are answers, not failures.
 */
export function isLiveReadFailure(reading: PlantTagIdValue): boolean {
  return isLiveSourceUnavailable(reading) || deriveLiveConnectivity(reading) === 'error'
}

/**
 * Folds one Live response into the kept readings. Merging the same
 * `fulfilledAt` again returns `previous`, and tags missing from the response
 * are dropped.
 */
export function mergeLiveReadings(
  previous: LiveReadings,
  readings: readonly PlantTagIdValue[],
  fulfilledAt: number
): LiveReadings {
  if (fulfilledAt === previous.mergedAt) return previous
  const entries = new Map<string, LiveReadingEntry>()
  for (const reading of readings) {
    if (!isLiveReadFailure(reading)) {
      entries.set(reading.nodeId, { reading, failure: undefined, failedPolls: 0 })
      continue
    }
    const entry = previous.entries.get(reading.nodeId)
    entries.set(reading.nodeId, {
      reading: entry?.reading,
      failure: reading,
      failedPolls: (entry?.failedPolls ?? 0) + 1
    })
  }
  const transportFailed = readings.some((reading) => reading.transportError)
  const pollFailed =
    transportFailed || readings.some((reading) => reading.errorCode === 'TAG_SOURCE_UNAVAILABLE')
  return {
    mergedAt: fulfilledAt,
    entries,
    failedPolls: pollFailed ? previous.failedPolls + 1 : 0,
    transportFailedPolls: transportFailed ? previous.transportFailedPolls + 1 : 0
  }
}

/**
 * What a row shows for a tag. Through the grace polls a row keeps its last
 * reading and pill. After that, a source it couldn't read shows Unavailable
 * with the last value, and any other failure shows as it came back.
 */
export function liveRowState(readings: LiveReadings, nodeId: string | undefined): LiveRowState {
  const entry = nodeId ? readings.entries.get(nodeId) : undefined
  const kept = entry?.reading
  const failure = entry?.failure
  if (!failure || (kept && entry.failedPolls <= LIVE_FAILURE_GRACE_POLLS)) {
    return { reading: kept, connectivity: deriveLiveConnectivity(kept), failure: undefined }
  }
  const connectivity = deriveLiveConnectivity(failure)
  if (kept && connectivity === 'unknown') {
    return { reading: kept, connectivity, failure: failure.error }
  }
  return { reading: failure, connectivity, failure: undefined }
}

/** Live poll interval: doubles with each consecutive failed poll, up to the cap. */
export function livePollIntervalMs(failedPolls: number): number {
  return Math.min(LIVE_POLL_INTERVAL_MS * 2 ** failedPolls, MAX_LIVE_POLL_INTERVAL_MS)
}
